/** The timing strip: seven cells, one grid, one hairline between them.
 *
 * The gaps are the background showing through a one-pixel grid gap rather
 * than borders on the cells, which is what lets the strip reflow from seven
 * across to two across without ever growing a double rule or losing one at an
 * edge. Every figure is tabular, because the strip is read by glancing at the
 * same digit position twice.
 *
 * What the numbers mean is decided in `interpolate.ts`; this only colours
 * them. A finished sector is accent when it is the quickest that sector has
 * been since the page connected and neutral otherwise, the sector in progress
 * counts up in the text colour at one decimal — three would be a blur — and a
 * sector this page has no honest figure for shows a dash rather than a zero.
 * A spectator who joins mid-lap sees dashes until the next lap starts, which
 * is the truth: nobody watched that sector happen.
 */

import { DASH, lapTimeSeconds } from '@/lib/format';

import { sectorCells } from './interpolate';
import type { CarState, LiveTiming, SectorCell } from './interpolate';

export interface TimingStripProps {
  timing: LiveTiming;
  car: CarState | null;
}

function Cell({
  label,
  value,
  size,
  colour,
  span,
}: {
  label: string;
  value: string;
  size: number;
  colour?: string;
  span?: number;
}) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        padding: '12px 16px',
        gridColumn: span ? `span ${span}` : undefined,
        minWidth: 0,
      }}
    >
      <dt
        style={{
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--color-neutral-500)',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: size,
          lineHeight: size >= 28 ? 1.1 : 1.3,
          fontVariantNumeric: 'tabular-nums',
          color: colour,
        }}
      >
        {value}
      </dd>
    </div>
  );
}

function sectorText(cell: SectorCell): string {
  if (cell.state === 'done') return cell.seconds.toFixed(3);
  if (cell.state === 'running') return cell.seconds.toFixed(1);
  return DASH;
}

function sectorColour(cell: SectorCell): string {
  if (cell.state === 'done') {
    return cell.best ? 'var(--color-accent-300)' : 'var(--color-neutral-200)';
  }
  if (cell.state === 'running') return 'var(--color-text)';
  return 'var(--color-neutral-500)';
}

export function TimingStrip({ timing, car }: TimingStripProps) {
  const lapTime = car ? car.lapTime : 0;
  const cells = sectorCells(timing, lapTime);
  const lap = car && car.lap > 0 ? String(car.lap) : DASH;

  return (
    <dl
      aria-label="Timing"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: 1,
        background: 'var(--color-divider)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        margin: 0,
      }}
    >
      <Cell label="lap" value={lap} size={28} />
      <Cell
        label="current lap"
        value={car ? lapTimeSeconds(car.lapTime) : DASH}
        size={28}
        colour="var(--color-accent-200)"
        span={2}
      />
      {cells.map((cell, index) => (
        <Cell
          key={index}
          label={`S${index + 1}`}
          value={sectorText(cell)}
          size={20}
          colour={sectorColour(cell)}
        />
      ))}
      <Cell label="last lap" value={lapTimeSeconds(timing.lastLap)} size={20} />
      <Cell
        label="best"
        value={lapTimeSeconds(timing.bestLap)}
        size={20}
        colour="var(--color-accent-300)"
      />
    </dl>
  );
}

export default TimingStrip;
